// Boss-Secretary profiles.
// Profile-level rule that overrides standard CFA/CFNA exceptions: external
// callers route to the Secretary first; whitelisted callers bypass.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, UserCheck, Power } from 'lucide-react';
import { api, getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function BossSecretary() {
  const me = api.me();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);

  const { data: list = [] } = useQuery({
    queryKey: ['boss-secretary', me?.domain_uuid],
    queryFn: () => me?.domain_uuid ? api.bossSecretary.list({ domain_uuid: me.domain_uuid }) : Promise.resolve([]),
    enabled: !!me?.domain_uuid,
  });

  // Pull extensions for the modal pickers
  const { data: extensions = [] } = useQuery<any[]>({
    queryKey: ['extensions-for-bs', me?.domain_uuid],
    queryFn: () => fetch(`/api/tenants/${me?.domain_uuid}/extensions`, auth()).then(r => r.json()),
    enabled: !!me?.domain_uuid,
  });

  const toggle = useMutation({
    mutationFn: (id: string) => api.bossSecretary.toggle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boss-secretary', me?.domain_uuid] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.bossSecretary.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boss-secretary', me?.domain_uuid] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><UserCheck /> Boss-Secretary Profiles</h1>
        <button className="btn-primary" onClick={() => setEditing({})}>
          <Plus size={16} className="inline mr-1" /> New Profile
        </button>
      </div>

      <div className="card p-3 text-sm text-textSecondary">
        Profile-level rule that overrides standard call-forwarding exceptions. External
        callers ring the Secretary first; the Boss's whitelist bypasses straight through.
        Boss does NOT see the ringing leg unless the Secretary forwards the call.
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th></th><th>Boss</th><th>Secretary</th><th>Whitelist</th><th>Timeout</th><th>Fallback</th><th></th></tr></thead>
          <tbody>
            {list.map((p: any) => (
              <tr key={p.profile_uuid} className="hover:bg-elevated cursor-pointer" onClick={() => setEditing({ ...p, extensions })}>
                <td>
                  <span className={`inline-block w-2 h-2 rounded-full ${p.enabled ? 'bg-success' : 'bg-textSecondary'}`} title={p.enabled ? 'enabled' : 'disabled'} />
                </td>
                <td>
                  <div className="font-medium">{p.boss_first_name} {p.boss_last_name}</div>
                  <div className="text-xs text-textSecondary font-mono">ext {p.boss_extension}</div>
                </td>
                <td>
                  <div className="font-medium">{p.secretary_first_name} {p.secretary_last_name}</div>
                  <div className="text-xs text-textSecondary font-mono">ext {p.secretary_extension}</div>
                </td>
                <td className="text-xs">
                  {(p.boss_whitelist || []).length > 0 ? (
                    <span className="badge">{p.boss_whitelist.length} numbers</span>
                  ) : <span className="text-textSecondary">none</span>}
                </td>
                <td className="text-xs">{p.secretary_screen_timeout}s</td>
                <td className="text-xs">{p.fallback_action}</td>
                <td onClick={e => e.stopPropagation()} className="space-x-1 whitespace-nowrap">
                  <button className="btn-ghost p-1" title={p.enabled ? 'Disable' : 'Enable'} onClick={() => toggle.mutate(p.profile_uuid)}>
                    <Power size={14} className={p.enabled ? 'text-success' : 'text-textSecondary'} />
                  </button>
                  <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete?') && remove.mutate(p.profile_uuid)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-textSecondary">No Boss-Secretary profiles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && me?.domain_uuid && (
        <EditModal
          domain_uuid={me.domain_uuid}
          profile={editing}
          extensions={extensions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function EditModal({ domain_uuid, profile, extensions, onClose }: any) {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>({
    boss_extension_uuid: profile.boss_extension_uuid || '',
    secretary_extension_uuid: profile.secretary_extension_uuid || '',
    enabled: profile.enabled !== false,
    boss_whitelist: profile.boss_whitelist || [],
    boss_blf_visibility: profile.boss_blf_visibility || 'hidden',
    secretary_screen_timeout: profile.secretary_screen_timeout || 30,
    fallback_action: profile.fallback_action || 'voicemail',
  });
  const [whitelistInput, setWhitelistInput] = useState('');

  const save = useMutation({
    mutationFn: () => profile.profile_uuid
      ? api.bossSecretary.update(profile.profile_uuid, form)
      : api.bossSecretary.create({ ...form, domain_uuid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boss-secretary', domain_uuid] });
      onClose();
    },
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  const addWhitelist = () => {
    const num = whitelistInput.trim();
    if (!num) return;
    set('boss_whitelist', [...(form.boss_whitelist || []), num]);
    setWhitelistInput('');
  };
  const removeWhitelist = (i: number) => {
    const next = [...form.boss_whitelist];
    next.splice(i, 1);
    set('boss_whitelist', next);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-xl w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{profile.profile_uuid ? 'Edit' : 'New'} Boss-Secretary</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <Field label="Boss extension *">
          <select className="input" value={form.boss_extension_uuid} onChange={e => set('boss_extension_uuid', e.target.value)} disabled={!!profile.profile_uuid}>
            <option value="">— select —</option>
            {extensions.map((e: any) => <option key={e.extension_uuid} value={e.extension_uuid}>{e.extension} — {e.directory_first_name} {e.directory_last_name}</option>)}
          </select>
        </Field>
        <Field label="Secretary extension *">
          <select className="input" value={form.secretary_extension_uuid} onChange={e => set('secretary_extension_uuid', e.target.value)}>
            <option value="">— select —</option>
            {extensions.map((e: any) => <option key={e.extension_uuid} value={e.extension_uuid}>{e.extension} — {e.directory_first_name} {e.directory_last_name}</option>)}
          </select>
        </Field>

        <Field label="Boss whitelist (caller numbers that bypass Secretary)" hint="Press Enter or click Add to append. Partial matches are allowed.">
          <div className="flex gap-2">
            <input className="input font-mono flex-1" placeholder="+15551234567 or 555" value={whitelistInput}
                   onChange={e => setWhitelistInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addWhitelist()} />
            <button className="btn" onClick={addWhitelist}>Add</button>
          </div>
          {form.boss_whitelist.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {form.boss_whitelist.map((w: string, i: number) => (
                <span key={i} className="badge bg-primary/20 text-primary inline-flex items-center gap-1">
                  {w}
                  <button onClick={() => removeWhitelist(i)} className="hover:text-danger"><X size={10} /></button>
                </span>
              ))}
            </div>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Boss BLF visibility">
            <select className="input" value={form.boss_blf_visibility} onChange={e => set('boss_blf_visibility', e.target.value)}>
              <option value="hidden">Hidden (default)</option>
              <option value="visible">Visible (Boss sees screening)</option>
            </select>
          </Field>
          <Field label="Secretary timeout (seconds)">
            <input className="input" type="number" min={5} max={300}
                   value={form.secretary_screen_timeout}
                   onChange={e => set('secretary_screen_timeout', parseInt(e.target.value, 10) || 30)} />
          </Field>
          <Field label="Fallback action">
            <select className="input" value={form.fallback_action} onChange={e => set('fallback_action', e.target.value)}>
              <option value="voicemail">Send to Boss voicemail</option>
              <option value="boss">Bridge to Boss directly</option>
              <option value="hangup">Hang up</option>
            </select>
          </Field>
          <Field label="Enabled">
            <label className="inline-flex items-center gap-2 mt-2">
              <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
              Profile is active
            </label>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!form.boss_extension_uuid || !form.secretary_extension_uuid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-textSecondary">{hint}</div>}
    </div>
  );
}
